/**
 * A type representing all the possible SQL enum drawer type string values.
 */
export enum CabinetDrawerType {
    'none',
    'fixed',
    'inset_fixed',
    'inner_custom',
}

/**
 * A cabinet that does not have any drawers.
 *
 * @type {CabinetDrawerType|string}
 */
export const DRAWER_TYPE_NONE = 'none';
/**
 * A cabinet with a fixed number of faced drawers.
 *
 * @type {CabinetDrawerType|string}
 */
export const DRAWER_TYPE_FIXED = 'fixed';
/**
 * A cabinet with a fixed number of faced inset drawers (flush with the
 * front of the carcase when closed.)
 *
 * @type {CabinetDrawerType|string}
 */
export const DRAWER_TYPE_INSET_FIXED = 'inset_fixed';
/**
 * A cabinet with a default number of customisable faced inset drawers
 * (flush with the front of the carcase when closed.)
 *
 * **NOTE**
 * Reserved for future customer drawer products that behave similarly to
 * customer inner drawers.
 *
 * @type {CabinetDrawerType|string}
 */
export const DRAWER_TYPE_INSET_CUSTOM = 'inset_custom';
/**
 * A cabinet with a fixed number of inner (faceless) drawers.
 *
 * @type {CabinetDrawerType|string}
 */
export const DRAWER_TYPE_INNER_FIXED = 'inner_fixed';
/**
 * A cabinet with a default number of customisable inner (faceless)
 * drawers.
 * @type {CabinetDrawerType|string}
 */
export const DRAWER_TYPE_INNER_CUSTOM = 'inner_custom';

export interface Cabinet {
    [key: string]: string | number | boolean | CabinetDrawerType;
    available_cabinet_tops?: string;
    available_doors?: string;
    available_drawers?: string;
    available_hinges?: string;
    available_shelves?: string;
    bifold?: number;
    allow_finger_pull?: boolean;
    code?: string;
    created_by?: string;
    created_date?: string;
    default_bottom_clearance?: number;
    default_lower_shelves?: number;
    default_middle_shelves?: number;
    default_return_width?: number;
    default_shelves?: number;
    default_top?: number;
    default_total_drawer_height?: number;
    default_upper_shelves?: number;
    default_height?: number;
    default_depth?: number;
    deleted?: number;
    deleted_date?: string;
    description?: string;
    door_hang?: string;
    doors?: number;
    drawer_type?: CabinetDrawerType | string;
    drawers?: number;
    inner_partition_offset?: number;
    edge_inner_label?: string;
    edge_l1_label?: string;
    edge_l2_label?: string;
    edge_w1_label?: string;
    edge_w2_label?: string;
    ext_label?: string;
    fixed_lower_partition_height?: number;
    fixed_microwave?: number;
    fixed_oven?: number;
    fixed_partition?: number;
    fixed_rangehood?: number;
    fixed_upper_partition_height?: number;
    gfp_allow_colour_edit?: number;
    gfp_description?: string;
    gfp_drawer1?: number;
    gfp_drawer2?: number;
    gfp_drawer3?: number;
    gfp_help_text?: number;
    gfp_hinge1?: number;
    gfp_hinge2?: number;
    gfp_hinge3?: number;
    gfp_long_description?: string;
    gfp_name?: number;
    gfp_product?: number;
    gfp_sub_style_id?: number;
    has_fascia?: number;
    has_form_fields?: number;
    hinge_direction?: number;
    image?: string;
    image_large?: string;
    int_label?: string;
    is_quick_flat_product?: number;
    is_robe_with_bottom?: number;
    key_shape?: number;
    lower_partition_height?: number;
    lshape?: number;
    manufacturer_availability?: string;
    manufacturer_description?: string;
    mat_type?: number;
    max_depth?: number;
    max_height?: number;
    max_width?: number;
    microwave?: number;
    min_depth?: number;
    min_height?: number;
    min_width?: number;
    minimum_opening_width?: number;
    modified_by?: string;
    modified_date?: string;
    name?: string;
    oven?: number;
    partition?: number;
    product_type?: number;
    rangehood?: number;
    requires_variation?: number;
    return_panel?: number;
    shelf_type?: number;
    shelves?: number;
    status?: number;
    style?: number;
    sub_style_id?: number;
    type?: number;
    upper_partition_height?: number;
    original_name?: string;
    manufacturer_cabinet_image?: string;
}
